<template>
  <v-dialog
    width="900px"
    persistent
    scrollable
    v-model="isOpened"
  >
    <v-card v-if="hasIntervention">
      <v-card-title>
        <v-icon small color="accent" class="mr-2 font-weight-bold">event</v-icon>
        Date d'intervention
        <v-spacer />
        <v-btn icon @click="isOpened = false">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-0">
        <v-row no-gutters justify="center" align="start">
          <div class="pa-4" style="width: 320px;">
            <v-checkbox
              v-model="isPlannedDaily"
              hide-details
              label="Journée(s) entière(s)"
              class="ma-0 pb-6"
            ></v-checkbox>
            <v-menu
              ref="menu"
              v-model="startDateMenu"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="interventionStartDateFormatted"
                  :label="isPlannedDaily ? `Date de début` : `Date`"
                  outlined
                  readonly
                  hide-details
                  class="pb-6"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="interventionStartDate"
                :first-day-of-week="1"
              ></v-date-picker>
            </v-menu>
            <v-menu
              ref="menu"
              v-model="endDateMenu"
              transition="scale-transition"
              offset-y
              min-width="auto"
              v-if="isPlannedDaily"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="interventionEndDateFormatted"
                  label="Date de fin"
                  outlined
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="interventionEndDate"
                :first-day-of-week="1"
              ></v-date-picker>
            </v-menu>
            <v-row no-gutters justify="center" align="center" v-else>
              <v-col class="pr-2">
                <v-select
                  v-model="interventionStartHour"
                  :items="dayHours"
                  label="Heure de début"
                  outlined
                  hide-details
                  menu-props="auto"
                />
              </v-col>
              <v-col class="pl-2">
                <v-select
                  v-model="interventionEndHour"
                  :items="endHourCandidates"
                  label="Heure de fin"
                  outlined
                  hide-details
                  menu-props="auto"
                />
              </v-col>
            </v-row>
          </div>
          <div style="align-self: stretch;">
            <v-divider vertical />
          </div>
          <v-col class="pa-6">
            <update-status
              v-model="statusId"
              :makerId="intervention.Maker_id"
              @change="storeStatus"
              class="pb-11"
            />
            <update-assignee
              v-model="assigneeId"
              :required="false"
              :intervention="intervention"
              @change="storeAssignee"
              class="pb-11"
            />
            <update-comment
              v-model="comment"
              :required="false"
              :intervention="intervention"
              v-if="!noComment"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions class="py-3 px-6">
        <v-spacer></v-spacer>
        <v-btn text @click="isOpened = false">Annuler</v-btn>
        <v-btn
          color="primary darken-1"
          :disabled="!canSubmit"
          :loading="isSaving"
          @click="submit"
        >
          {{ submitText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import dayHours from '@/dayHours.js';
import UpdateStatus from './Update/Status.vue';
import UpdateAssignee from './Update/Assignee.vue';
import UpdateComment from './Update/Comment.vue';

export default {
  props: {
    value: Boolean,
    intervention: {
      type: Object,
      required: false,
    },
    noComment: {
      type: Boolean,
      default: false,
    },
    catchSubmit: {
      type: Boolean,
      default: false,
    },
    defaultStatusId: {
      type: Number,
      required: false,
    },
    defaultAssignee: {
      type: Object,
      required: false,
    },
    submitText: {
      type: String,
      default: `Mettre à jour l'intervention`,
    },
  },
  data: () => ({
    dayHours,
    isSaving: false,
    startDateMenu: null,
    endDateMenu: null,
    interventionStartDate: null,
    interventionStartDateFormatted: null,
    interventionEndDate: null,
    interventionEndDateFormatted: null,
    interventionStartHour: null,
    interventionEndHour: null,
    isPlannedDaily: true,
    status: null,
    statusId: null,
    assignee: null,
    assigneeId: null,
    comment: ``,
  }),
  computed: {
    isOpened: {
      get() { return this.value; },
      set(value) { this.$emit(`input`, value); },
    },
    hasIntervention() {
      return Boolean(this.intervention && this.intervention.id);
    },
    canSubmit() {
      return true;
      // return Boolean(this.comment && this.comment.length);
    },
    endHourCandidates() {
      if (!this.interventionStartHour) { return []; }
      const startHourIndex = _.indexOf(this.dayHours, this.interventionStartHour);
      return _.map(this.dayHours, (hour, i) => ({
        text: hour,
        value: hour,
        disabled: i <= startHourIndex,
      }));
    },
  },
  methods: {
    setInterventionDatas() {
      this.setInterventionStartDate();
      this.setInterventionEndDate();
      this.setIsPlannedDaily();
      this.setStatus();
      this.setAssignee();
      this.setNearestStartHour();
    },
    setInterventionStartDate() {
      let interventionStartDate = moment();
      if (this.hasIntervention && this.intervention.plannedStartAt) {
        interventionStartDate = moment(this.intervention.plannedStartAt, `YYYYMMDDHHmm`);
        if (!interventionStartDate.isValid()) {
          interventionStartDate = moment();
        }
      }

      this.interventionStartDate = interventionStartDate.format(`YYYY-MM-DD`);
    },
    setInterventionEndDate() {
      let interventionEndDate = moment();
      if (this.hasIntervention && this.intervention.plannedEndAt) {
        interventionEndDate = moment(this.intervention.plannedEndAt, `YYYYMMDDHHmm`);
        if (!interventionEndDate.isValid()) {
          interventionEndDate = moment(this.intervention.plannedStartAt, `YYYYMMDDHHmm`);
        }
        if (!interventionEndDate.isValid()) {
          interventionEndDate = moment();
        }
      }

      this.interventionEndDate = interventionEndDate.format(`YYYY-MM-DD`);
    },
    setIsPlannedDaily() {
      this.isPlannedDaily = Boolean(this.hasIntervention && !this.intervention.isPlannedHourly);
    },
    setStatus() {
      this.statusId = null;
      if (this.defaultStatusId) {
        this.statusId = this.defaultStatusId;
      } else if (this.hasIntervention) {
        this.status = this.intervention.status || null;
        this.statusId = this.intervention.status?.id || null;
      }
    },
    setAssignee() {
      this.assigneeId = null;
      if (this.defaultAssignee) {
        this.assignee = this.defaultAssignee;
        this.assigneeId = this.defaultAssignee.id;
      } else if (this.hasIntervention) {
        this.assignee = this.intervention.assignee || null;
        this.assigneeId = this.intervention.assignee?.id || null;
      }
    },
    setNearestStartHour() {
      this.interventionStartHour = moment().add(1, `hour`).startOf(`hour`).format(`HH:mm`);
    },
    setDefaultEndHour() {
      if (this.interventionStartHour) {
        this.interventionEndHour = moment(this.interventionStartHour, `HH:mm`).add(1, `hour`).format(`HH:mm`);
        const startHourIndex = _.indexOf(this.dayHours, this.interventionStartHour);
        const endHourIndex = _.indexOf(this.dayHours, this.interventionEndHour);
        if (endHourIndex < startHourIndex) {
          this.interventionEndHour = _.last(this.dayHours);
        }
      }
    },
    storeStatus(status) {
      this.status = status;
    },
    storeAssignee(assignee) {
      this.assignee = assignee;
    },
    async submit() {
      this.isSaving = true;
      const interventionStartDate = moment(this.interventionStartDate, `YYYY-MM-DD`).format(`YYYYMMDD`);
      const interventionEndDate = moment(this.interventionEndDate, `YYYY-MM-DD`).format(`YYYYMMDD`);
      let plannedStartAt = `${interventionStartDate}0000`;
      let plannedEndAt = `${interventionEndDate}0000`;
      if (!this.isPlannedDaily) {
        const interventionStartHour = moment(this.interventionStartHour, `HH:mm`).format(`HHmm`);
        const interventionEndHour = moment(this.interventionEndHour, `HH:mm`).format(`HHmm`);
        plannedStartAt = `${interventionStartDate}${interventionStartHour}`;
        plannedEndAt = `${interventionEndDate}${interventionEndHour}`;
      }

      const formattedDate = this.$options.filters.formatInterventionDate({
        plannedStartAt,
        plannedEndAt,
        lower: true,
      });
      const dateComment = `Intervention planifiée ${formattedDate}\n`;
      let assigneeComment = ``;
      if (this.assigneeId) {
        assigneeComment = `Intervention assignée à ${this.assignee.firstname} ${this.assignee.lastname}\n`;
      }

      let { comment } = this;
      if (!this.noComment) {
        comment = `${dateComment}${assigneeComment}\n${comment}`;
      }

      if (this.catchSubmit) {
        return this.$emit(`submit`, {
          plannedStartAt,
          plannedEndAt,
          isPlannedHourly: !this.isPlannedDaily,
          statusId: this.statusId,
          comment,
          assigneeId: this.assigneeId,
        });
      }

      const promises = [
        this.$store.dispatch(`ui/intervention/updatePlannedDate`, {
          plannedStartAt,
          plannedEndAt,
          isPlannedHourly: !this.isPlannedDaily,
        }),
        this.$store.dispatch(`model/intervention/addStep`, {
          interventionId: this.intervention.id,
          statusId: this.statusId,
          comment,
        }),
      ];

      if (this.assigneeId) {
        promises.push(
          this.$store.dispatch(`model/intervention/changeAssignee`, {
            interventionId: this.intervention.id,
            assigneeId: this.assigneeId,
          })
        );
      }

      await Promise.all(promises);
      return this.$emit(`updated`);
    },
  },
  beforeMount() {
    this.setInterventionDatas();
  },
  watch: {
    isOpened() {
      this.setInterventionDatas();
      this.comment = ``;
      setTimeout(() => { this.isSaving = false; }, 300);
    },
    intervention() {
      this.setInterventionDatas();
    },
    isPlannedDaily(isPlannedDaily) {
      if (!isPlannedDaily) {
        this.interventionEndDate = this.interventionStartDate;
      }
    },
    interventionStartDate(date, previousDate) {
      if (!date) { this.interventionStartDateFormatted = null; }
      this.interventionStartDateFormatted = moment(date, `YYYY-MM-DD`).format(`DD/MM/YYYY`);
      const previousDateMoment = moment(previousDate, `YYYY-MM-DD`);
      const endDateMoment = moment(this.interventionEndDate, `YYYY-MM-DD`);
      if (!endDateMoment.isBefore(previousDateMoment)) {
        const previousDaysDuration = endDateMoment.diff(previousDateMoment, `days`);
        this.interventionEndDate = moment(date, `YYYY-MM-DD`).add(previousDaysDuration, `days`).format(`YYYY-MM-DD`);
      }
    },
    interventionEndDate(date) {
      if (!date) {
        this.interventionEndDateFormatted = null;
      }
      const endDateMoment = moment(date, `YYYY-MM-DD`);
      const startDateMoment = moment(this.interventionStartDate, `YYYY-MM-DD`);
      this.interventionEndDateFormatted = endDateMoment.format(`DD/MM/YYYY`);
      if (endDateMoment.isBefore(startDateMoment)) {
        this.interventionStartDate = endDateMoment.format(`YYYY-MM-DD`);
        this.interventionStartDateFormatted = endDateMoment.format(`DD/MM/YYYY`);
      }
    },
    interventionStartHour() {
      this.setDefaultEndHour();
    },
  },
  components: {
    UpdateStatus,
    UpdateAssignee,
    UpdateComment,
  },
};
</script>

<style lang="scss">
</style>
